import { Events, UICorePlugin } from '@clappr/core';

import './airplay-button.scss';

const AVAILABILITY_EVENT = 'webkitplaybacktargetavailabilitychanged';
const WIRELESS_EVENT = 'webkitcurrentplaybacktargetiswirelesschanged';

const ICON = `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
  <path d="M6 17H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-2" fill="none" stroke="currentColor" stroke-width="2"/>
  <path d="M12 14l6 7H6z" fill="currentColor"/>
</svg>`;

// Opens Safari's AirPlay device picker. Our media control replaces Safari's native
// controls, which is where AirPlay would otherwise live. Safari tells the <video>
// whether any receivers are reachable, so the button stays hidden until one is.
// The <video> only offers AirPlay with remote playback enabled; see ../airplay.js.
export default class AirPlayButton extends UICorePlugin {
  get supportedVersion() {
    return { min: '0.13.2' };
  }

  get name() {
    return 'airplay_button';
  }

  get tagName() {
    return 'button';
  }

  get attributes() {
    return {
      type: 'button',
      class: 'boxcast-clappr-button airplay-button',
      'data-airplay-button': '',
      'aria-label': 'AirPlay',
    };
  }

  get events() {
    return { click: 'showPicker' };
  }

  bindEvents() {
    this.listenTo(this.core.mediaControl, Events.MEDIACONTROL_RENDERED, this.render);
    this.listenTo(this.core, Events.CORE_ACTIVE_CONTAINER_CHANGED, this._bindVideo);
    if (this.core.isReady) {
      this._bindVideo();
    } else {
      this.listenTo(this.core, Events.CORE_READY, this._bindVideo);
    }
  }

  render() {
    this.el.innerHTML = ICON;
    this._updateState();
    this.core.mediaControl.$('.media-control-right-panel').append(this.el);
    return this;
  }

  showPicker() {
    if (this._video) {
      this._video.webkitShowPlaybackTargetPicker();
    }
  }

  destroy() {
    this._unbindVideo();
    return super.destroy();
  }

  _bindVideo() {
    this._unbindVideo();
    const playback = this.core.activePlayback;
    const video = playback && playback.el;
    if (!video || !video.webkitShowPlaybackTargetPicker) {
      return;
    }
    this._video = video;
    this._videoListeners = {
      [AVAILABILITY_EVENT]: (e) => {
        this._available = e.availability === 'available';
        this._updateState();
      },
      [WIRELESS_EVENT]: () => this._updateState(),
    };
    Object.keys(this._videoListeners).forEach((name) =>
      video.addEventListener(name, this._videoListeners[name])
    );
  }

  _unbindVideo() {
    if (this._video) {
      Object.keys(this._videoListeners).forEach((name) =>
        this._video.removeEventListener(name, this._videoListeners[name])
      );
    }
    this._video = null;
    this._videoListeners = null;
    this._available = false;
    this._updateState();
  }

  _updateState() {
    if (!this.el) {
      return;
    }
    const wireless = !!(this._video && this._video.webkitCurrentPlaybackTargetIsWireless);
    // stay visible while playing on a receiver so the viewer can switch back
    this.el.style.display = this._available || wireless ? '' : 'none';
    this.el.classList.toggle('airplay-active', wireless);
  }
}

AirPlayButton.type = 'core';
